import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Label,
  LabelProps
} from 'recharts';
import dynamicClass from 'classnames';
import { useOptimizely } from '~/context/Optimizely';
import { usePublicRuntimeConfig } from '~/hooks/usePublicRuntimeConfig';
import { useRef } from 'react';
import { CartesianViewBox } from 'recharts/types/util/types';
import classNames from 'classnames';

const renderCustomXAxisTick = (props: any) => {
  const { x, y, payload, index, visibleTicksCount } = props;
  let dx = 0;
  let textAnchor = 'middle';

  if (index === 0) {
    // First tick
    textAnchor = 'start';
    dx = 1;
  } else if (index === visibleTicksCount - 1) {
    // Last tick
    textAnchor = 'end';
    dx = -1;
  }

  return (
    <text x={x} y={y + 20} dx={dx} textAnchor={textAnchor} fill="#000">
      {payload.value}
    </text>
  );
};

const formatSavings = (value: number) => {
  if (value >= 1_000_000_000) {
    return `$${(value / 1_000_000_000).toFixed(0)}B`; // Format
  } else if (value >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(0)}M`; // Format in milli
  } else if (value >= 100_000) {
    return `$${(value / 1000).toFixed(0)}k`;
  } else {
    return value > 1000
      ? `$${(value / 1000).toFixed(0)}k`
      : `$${Math.round(value / 10) * 10}`;
  }
};

const isCartesianViewBox = (
  viewBox: LabelProps['viewBox']
): viewBox is CartesianViewBox => {
  return (
    viewBox !== undefined &&
    'x' in viewBox &&
    'y' in viewBox &&
    'width' in viewBox &&
    'height' in viewBox
  );
};

interface CustomAxisLabelProps {
  x: number;
  y: number;
  width?: number;
  height: number;
}

const CustomYAxisLabel: React.FC<CustomAxisLabelProps> = () => {
  const textRef = useRef<SVGTextElement>(null);

  return (
    <g transform={`translate(0, -15)`}>
      {/* Centered text */}
      <text
        ref={textRef}
        x={50}
        y={-4}
        textAnchor="middle"
        fill="#5E6161"
        className="typography-body-3 font-light text-on-secondary-surface-subdued"
        // transform="rotate(-90)"
      >
        {'Est. total savings'}
      </text>
    </g>
  );
};

const CustomXAxisLabel: React.FC<CustomAxisLabelProps> = ({ x, y, width }) => {
  const textRef = useRef<SVGTextElement>(null);

  if (!width) return null;

  const lineY = y + 50; // Adjust as needed to position below tick labels

  return (
    <g transform={`translate(${x}, ${lineY})`}>
      {/* Centered text */}
      <text
        ref={textRef}
        x={30}
        y={5}
        textAnchor="middle"
        fill="#5E6161"
        className="typography-body-3 font-light text-on-secondary-surface-subdued"
      >
        {'Age in years'}
      </text>
    </g>
  );
};

const renderCustomLegend = (
  ctaColor?: string,
  ctaText?: string,
  ctaTextColor?: string,
  registrationButton?: boolean,
  ctaDestination?: string,
  webregURL?: string
) => (
  <div className="mt-10 flex items-center justify-center pr-2 tablet:mt-12 tablet:justify-start tablet:pl-2 desktop:pl-0">
    <div className="mr-4 flex h-fit items-center font-medium">
      <div className="mr-1 size-[14px] rounded-full bg-brand-base-dark" />
      <span className="text-sm">Saved</span>
    </div>
    <div className="flex h-fit items-center font-medium">
      <div className="mr-2 size-[14px] rounded-full bg-brand-base" />
      <span className="text-sm">Earned</span>
    </div>
    {ctaText && (
      <button
        className={`typography-button-md absolute ml-auto hidden min-w-32 items-center justify-center rounded-button-shape p-4 text-center font-medium leading-4 tablet:static tablet:flex`}
        {...((ctaColor || ctaTextColor) && {
          style: { backgroundColor: ctaColor, color: ctaTextColor }
        })}
      >
        <a type="button" href={registrationButton ? webregURL : ctaDestination}>
          {ctaText}
        </a>
      </button>
    )}
  </div>
);

export interface SavingsCalculatorChartProps {
  chartData: any;
  estimatedSavings: number;
  savingsAgeRange: [number, number];
  hideGraphOnMobile?: boolean;
  chartTitle?: string;
  ctaColor?: string;
  ctaText?: string;
  ctaTextColor?: string;
  registrationButton?: boolean;
  ctaDestination?: string;
}

const renderActiveDot = (props: any, maxAge: number, minAge: number) => {
  const { cx, cy } = props;
  return (
    <>
      <g transform={`translate(${cx}, ${cy})`}>
        <g
          transform={dynamicClass({
            'translate(-72, -95)':
              props.payload.age > minAge && props.payload.age < maxAge,
            'translate(5, -95)': props.payload.age === minAge,
            'translate(-175, 15)': props.payload.age === maxAge
          })}
        >
          <svg width="auto" height="auto" xmlns="http://www.w3.org/2000/svg">
            <foreignObject x="0" y="0" width="100%" height="100%">
              <div className="inline-flex h-[94px] flex-col items-start justify-center gap-3 rounded-input-box-shape bg-white p-3 shadow-line-dark">
                <div className="text-sm font-medium">
                  Savings by age {props.payload.age}
                </div>
                <div className="flex flex-col items-center justify-start gap-2">
                  <div className="inline-flex items-center justify-start gap-0.5 self-stretch rounded-[40px] bg-white pr-2">
                    <div className="flex h-5 w-5 items-center justify-center">
                      <div className="relative h-[14px] w-[14px] rounded-full bg-brand-base-dark" />
                    </div>
                    <div className="text-center text-sm leading-[14px]">
                      ${props.payload.saved} Saved
                    </div>
                  </div>
                  <div className="inline-flex items-center justify-start gap-0.5 self-stretch rounded-[40px] bg-white pr-2">
                    <div className="flex h-5 w-5 items-center justify-center">
                      <div className="relative h-[14px] w-[14px] rounded-full bg-brand-base" />
                    </div>
                    <div className="text-center text-sm leading-[14px]">
                      ${props.payload.earned} Earned
                    </div>
                  </div>
                </div>
              </div>
            </foreignObject>
          </svg>
        </g>
      </g>
    </>
  );
};

export const SavingsChart = ({
  chartData,
  estimatedSavings,
  savingsAgeRange,
  hideGraphOnMobile,
  chartTitle,
  ctaColor,
  ctaText,
  ctaTextColor,
  registrationButton,
  ctaDestination
}: SavingsCalculatorChartProps) => {
  const experiments = useOptimizely();

  const { webregUrl: fallBackWebregUrl, onboardingUrl } =
    usePublicRuntimeConfig();

  const webregURL = experiments?.website_onboarding_initialize?.enabled
    ? onboardingUrl
    : fallBackWebregUrl;

  return (
    <div
      className={classNames('w-full rounded-2xl desktop:w-2/3', {
        'hidden tablet:block': hideGraphOnMobile
      })}
    >
      <div className="mb-4 pl-9 tablet:mb-12">
        <p className="mb-2 text-center text-base font-medium tablet:text-start desktop:mb-4">
          {chartTitle} from {savingsAgeRange[0]} - {savingsAgeRange[1]} years
          old
        </p>
        <h2 className="heading-display hidden desktop:block">
          {new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          }).format(estimatedSavings)}
        </h2>
      </div>
      <ResponsiveContainer
        width="100%"
        height={436}
        className="relative overflow-visible pl-9"
      >
        <AreaChart
          data={chartData}
          className="overflow-visible [&>svg]:overflow-visible"
        >
          <XAxis
            dataKey="age"
            domain={['dataMin', 'dataMax']}
            padding={{ left: 0, right: 0 }}
            scale="point"
            interval={0}
            tick={renderCustomXAxisTick}
            tickLine={false}
            axisLine={{ stroke: '#d2d9d9' }}
          >
            <Label
              content={(props) => {
                const { viewBox } = props;
                if (isCartesianViewBox(viewBox)) {
                  return (
                    <CustomXAxisLabel
                      x={viewBox?.x ?? 0}
                      y={viewBox?.y ?? 0}
                      width={viewBox?.width ?? 0}
                      height={viewBox?.height ?? 0}
                    />
                  );
                }
              }}
              position="insideBottom"
              offset={-30} // Adjust as needed
            />
          </XAxis>
          <YAxis
            domain={[0, estimatedSavings]}
            tickFormatter={formatSavings}
            className="!w-8 overflow-visible"
            interval="preserveStartEnd"
            ticks={[
              0,
              estimatedSavings * 0.25,
              estimatedSavings * 0.5,
              estimatedSavings * 0.75,
              estimatedSavings
            ]}
            tick={{
              fill: '#000',
              dx: -50,
              textAnchor: 'start'
            }}
            axisLine={false}
            tickLine={false}
          >
            <Label
              content={(props) => {
                const { viewBox } = props;
                if (isCartesianViewBox(viewBox)) {
                  return (
                    <CustomYAxisLabel
                      x={viewBox?.x ?? 0}
                      y={viewBox?.y ?? 0}
                      height={viewBox?.height ?? 0}
                    />
                  );
                }
              }}
              position="insideLeft"
              offset={10} // Adjust as needed
            />
          </YAxis>
          <Area
            type="monotone"
            dataKey="total"
            stroke="#00D9B4"
            fillOpacity={1}
            fill="#00D9B4"
            activeDot={false}
          />
          <Area
            type="monotone"
            dataKey="saved"
            stroke="#0A7266"
            fillOpacity={1}
            fill="#0A7266"
            className="z-0"
            activeDot={false}
          />
          <CartesianGrid stroke="#1A353826" horizontal vertical={false} />
          <Tooltip
            wrapperClassName="hidden"
            cursor={{
              stroke: '#035C67',
              strokeWidth: 1,
              strokeDasharray: '5 5'
            }}
          />
          <Area
            type="monotone"
            dataKey="total"
            stroke="transparent"
            fillOpacity={1}
            fill="transparent"
            className="z-0"
            activeDot={(props: any) =>
              renderActiveDot(props, savingsAgeRange[1], savingsAgeRange[0])
            }
          />
          <Legend
            content={renderCustomLegend(
              ctaColor,
              ctaText,
              ctaTextColor,
              registrationButton,
              ctaDestination,
              webregURL
            )}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};
